import { useState } from "react";
import { computePositionSize } from "../lib/position";

interface Props {
  entry: string;
  stop: string;
  target?: string;
  riskPct: string;
  initialAccountSize?: string;
}

const KEY = "alpha.accountSize";

function remembered(fallback: string): string {
  try {
    if (typeof localStorage === "undefined") return fallback;
    return localStorage.getItem(KEY) ?? fallback;
  } catch {
    return fallback;
  }
}

const money = (n: number) => new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 2 }).format(n);

/** Shares for a given account size and risk percent, from the entry and stop levels. A sizing aid, not a recommendation. */
export function PositionSizeHelper({ entry, stop, target, riskPct, initialAccountSize = "100000" }: Props) {
  const [account, setAccount] = useState(() => remembered(initialAccountSize));
  const result = computePositionSize({
    accountSize: Number(account),
    riskPct: Number(riskPct),
    entry: Number(entry),
    stop: Number(stop),
    target: target ? Number(target) : undefined,
  });

  return (
    <section className="card" aria-label="Position size helper">
      <h3>Position size</h3>
      <label>
        Account size
        <input
          inputMode="decimal"
          value={account}
          onChange={(e) => {
            setAccount(e.target.value);
            try {
              localStorage.setItem(KEY, e.target.value);
            } catch {
              /* storage unavailable: the value just is not remembered */
            }
          }}
        />
      </label>
      {result ? (
        <dl className="kv" data-testid="position-result">
          <dt>Risk budget</dt>
          <dd className="num neg">{money(result.riskAmount)}</dd>
          <dt>Risk per share</dt>
          <dd className="num neg">{money(result.perShareRisk)}</dd>
          <dt>Shares</dt>
          <dd className="num" data-testid="shares">{result.shares}</dd>
          <dt>Position value</dt>
          <dd className="num">{money(result.positionValue)}</dd>
          {result.rewardRisk !== null && (
            <>
              <dt>Reward to risk</dt>
              <dd className="num pos">{result.rewardRisk.toFixed(2)}</dd>
            </>
          )}
        </dl>
      ) : (
        <p className="muted">Enter account size, risk percent, entry and a stop that differs from entry.</p>
      )}
    </section>
  );
}
